import { useCallback, useEffect, useState } from 'react';
import api from '@/shared/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

/**
 * Rattrapage des déclarations Enedis : les mandats signés jamais déclarés, et les
 * dossiers restés en attente chez Energrid.
 *
 * APERÇU D'ABORD. `GET /admin/enedis/rattrapage` n'appelle personne : il liste. Le
 * bouton seul pose des déclarations, et elles sont NOMINATIVES chez un tiers (nom et
 * adresse électronique du titulaire), irrévocables tant qu'Enedis n'a pas accordé.
 *
 * UNE REQUÊTE PAR SECONDE, PLAFONNÉE. Le quota d'Energrid n'est pas publié : le serveur
 * tient le rythme, et chaque lancement fait au plus `limite` requêtes. Ce qui reste
 * s'affiche, on relance.
 */
const LIMITE = 30;

function Liste({ titre, lignes, vide, rendu }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">{titre} ({lignes.length})</CardTitle></CardHeader>
      <CardContent>
        {lignes.length === 0
          ? <p className="text-[13px] text-neutral-400">{vide}</p>
          : <ul className="divide-y divide-neutral-100">{lignes.map(rendu)}</ul>}
      </CardContent>
    </Card>
  );
}

export default function RattrapageEnedisPage() {
  const [apercu, setApercu] = useState(null);
  const [resultat, setResultat] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await api.get('/admin/enedis/rattrapage');
      setApercu(r.data);
    } catch {
      toast.error("L'aperçu du rattrapage n'a pas pu être chargé.");
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const lancer = async () => {
    setEnCours(true);
    try {
      const r = await api.post('/admin/enedis/rattrapage', { limite: LIMITE });
      setResultat(r.data);
      toast.success(`${r.data.appels} requête(s) envoyée(s) à Energrid.`);
      await charger();
    } catch (err) {
      // 409 : un autre rattrapage tourne pour ce sous-compte (autre admin, autre onglet).
      // Le serveur le refuse pour ne jamais poser deux fois la même déclaration.
      toast.error(err.response?.status === 409
        ? err.response.data.detail
        : "Le rattrapage s'est interrompu : relancez-le, l'aperçu dit ce qui reste.");
    } finally {
      setEnCours(false);
    }
  };

  if (!apercu) return <p className="p-6 text-sm text-neutral-400">Chargement…</p>;
  const total = apercu.a_declarer.length + apercu.en_attente.length;
  return (
    <div className="p-6 space-y-4 max-w-4xl" data-testid="rattrapage-enedis">
      <div>
        <h1 className="text-xl font-semibold">Rattrapage Enedis</h1>
        <p className="text-[13px] text-neutral-500 mt-1">
          Déclare les mandats signés qui ne l'ont jamais été et relit les dossiers en
          attente. Une requête par seconde, {LIMITE} au plus par lancement.
        </p>
      </div>
      <Button onClick={lancer} disabled={enCours || total === 0} data-testid="rattrapage-enedis-lancer">
        <RefreshCw className={`w-4 h-4 mr-2 ${enCours ? 'animate-spin' : ''}`} />
        {enCours ? 'Rattrapage en cours…' : `Lancer le rattrapage (${total} élément(s))`}
      </Button>
      {resultat && (
        <p className="text-[13px] text-neutral-600">
          Dernier lancement : {resultat.declares.length} mandat(s) traité(s),
          {' '}{resultat.relus.length} dossier(s) relu(s), {resultat.reste} restant(s).
        </p>
      )}
      <Liste titre="Mandats signés à déclarer" lignes={apercu.a_declarer}
             vide="Tous les mandats signés sont déclarés."
             rendu={(m) => (
               <li key={m.mandat_id} className="py-2 flex justify-between text-[13px]">
                 <span>{m.client || 'Client sans nom'}</span>
                 <span className="text-neutral-500">{m.raison}</span>
               </li>
             )} />
      <Liste titre="Dossiers en attente chez Energrid" lignes={apercu.en_attente}
             vide="Aucun dossier en attente."
             rendu={(d) => (
               <li key={d.consentement_id} className="py-2 flex justify-between text-[13px]">
                 <span>{d.client || 'Titulaire sans fiche'}</span>
                 <span className="text-neutral-500">{d.etat}</span>
               </li>
             )} />
    </div>
  );
}
